import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Phone, PhoneOff, Ear, Scissors } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, errMsg, BACKEND, fmtTime } from "@/lib/api";
import { PageHead, card, Field, PatientSelect, ResultBadge, FlagList } from "@/components/admin/kit";
import { Wave } from "@/components/site/IncomingCallCard";
import { CallTypePicker, callBody, KINDS } from "@/components/admin/CallTypePicker";
import { useLoad } from "@/lib/api";

const Key = ({ k, label, onClick, testid, tone }) => (
  <button onClick={onClick} data-testid={testid} className={`grid place-items-center rounded-2xl px-2 py-3 text-white transition-transform active:scale-95 ${tone}`}>
    <span className="text-xl font-extrabold leading-none">{k}</span><span className="mt-1 text-center text-[10px] leading-tight opacity-90">{label}</span>
  </button>
);

const ENDED = { listened_full: "Answered · listened fully", hung_up_early: "Answered · hung up early", not_answered: "Not answered", "9": "Pressed 9 · calls stopped" };

export default function Simulator() {
  const api = useApi();
  const audio = useRef(new Audio());
  const [q, setQ] = useState({ patient_id: "", kind: "daily", slot: "morning", medicine_id: "", appointment_id: "" });
  const [presets] = useLoad("/sim/presets");
  const [call, setCall] = useState(null);
  const [flags, setFlags] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const a = audio.current;
    a.onended = () => setPlaying(false);
    return () => a.pause();
  }, []);

  const playScript = (s) => {
    const a = audio.current;
    a.pause();
    setPlaying(false);
    if (s?.audio_current) {
      a.src = `${BACKEND}/api/audio/${s.id}/phone.wav?v=${s.audio.hash}`;
      a.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const start = async (body = callBody(q)) => {
    try {
      const r = (await api.post("/sim/start", body)).data;
      setCall({ log: r.log, script: r.script, ended: null });
      setFlags(null);
      playScript(r.script);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const act = async (action) => {
    try {
      const r = (await api.post(`/sim/${call.log.id}/action`, { action })).data;
      setFlags(r.flags);
      if (action === "2") { setCall({ ...call, log: r.log }); playScript(r.script); return; }
      setCall({ log: r.log, script: r.script, ended: action, alert: r.alert });
      action === "9" ? playScript(r.script) : audio.current.pause();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const l = call?.log;
  return (
    <div data-testid="simulator-page">
      <PageHead title="Call simulator" sub="One call per dose time. No retries. Works without any phone provider — every outcome is written to the call log." />
      <div className={`${card} mb-4`} data-testid="sim-presets">
        <p className="mb-2 text-sm font-bold">Try each call type <span className="font-normal text-muted-foreground">— made-up sample patients, no real people</span></p>
        <div className="flex flex-wrap gap-2">
          {(presets || []).map((p) => (
            <Button key={p.key} variant="outline" size="sm" onClick={() => { const b = { patient_id: p.patient_id, kind: p.kind, slot: p.slot, medicine_id: p.medicine_id, appointment_id: p.appointment_id }; setQ({ ...q, ...b }); start(b); }} data-testid={`sim-preset-${p.key}`}>{p.label}</Button>
          ))}
        </div>
      </div>
      <div className={`${card} grid gap-3 sm:grid-cols-4`}>
        <Field label="Patient"><PatientSelect value={q.patient_id} onChange={(v) => setQ({ ...q, patient_id: v, medicine_id: "", appointment_id: "" })} testid="sim-patient-select" /></Field>
        <CallTypePicker q={q} setQ={setQ} idp="sim" />
        <div className="flex items-end"><Button className="w-full" disabled={!q.patient_id} onClick={() => start()} data-testid="sim-start-btn"><Phone size={16} /> Place call</Button></div>
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="phone-frame mx-auto" data-testid="sim-phone">
          <div className="phone-screen flex flex-col bg-gradient-to-b from-[var(--plum)] to-[var(--stage)] p-5 text-[var(--cream)]">
            {!call ? (
              <div className="m-auto text-center text-sm text-[var(--cream)]/60"><Phone className="mx-auto mb-3" />Choose a patient and place a call</div>
            ) : (
              <div className="flex flex-1 flex-col">
                <p className="text-center text-xs uppercase tracking-widest text-[var(--cream)]/60">{call.ended === "9" ? "Stop confirmation" : (KINDS.find((k) => k[0] === l.kind) || [])[1]}</p>
                <p className="mt-1 text-center font-semibold">OnDose{l.slot ? ` · ${l.slot}` : ""}</p>
                <div className="mx-auto my-3"><Wave on={playing} n={16} /></div>
                <p className="font-te flex-1 overflow-y-auto rounded-2xl bg-white/10 p-3 text-[12.5px] leading-relaxed" data-testid={call.ended === "9" ? "sim-stop-confirm-text" : "sim-script-text"}>{call.script?.text}</p>
                {!call.script?.audio_current && <p className="mt-1 text-center text-[10px] text-[var(--cream)]/50">Voice not generated — showing text</p>}
                {call.ended ? (
                  <p className="mt-3 rounded-xl bg-white/10 py-2 text-center text-sm font-semibold" data-testid="sim-ended">{ENDED[call.ended]}</p>
                ) : (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <Key k={<Ear size={20} />} label="Answered (listened fully)" tone="bg-green-600" onClick={() => act("listened_full")} testid="sim-listened-full" />
                    <Key k={<Scissors size={20} />} label="Answered (hung up early)" tone="bg-amber-600" onClick={() => act("hung_up_early")} testid="sim-hung-up-early" />
                    <Key k={<PhoneOff size={20} />} label="Not answered" tone="bg-red-600" onClick={() => act("not_answered")} testid="sim-not-answered" />
                    <Key k="2" label="Replay" tone="bg-sky-600" onClick={() => act("2")} testid="sim-press-2" />
                    <Key k="9" label="Stop calls" tone="col-span-2 bg-purple-700" onClick={() => act("9")} testid="sim-press-9" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="grid content-start gap-4">
          <div className={card} data-testid="sim-log-panel">
            <p className="mb-3 font-bold">Call log entry</p>
            {!l ? <p className="text-sm text-muted-foreground">No call yet.</p> : (
              <div className="rounded-2xl bg-[var(--lav)] p-3" data-testid="sim-log-entry">
                <div className="flex flex-wrap items-center gap-2 text-sm"><b>{l.kind}{l.slot ? ` · ${l.slot}` : ""}</b><ResultBadge r={l.result} />
                  {l.result !== "pending" && <span className="text-xs" data-testid="sim-log-listen">listened {l.listen_seconds}s of {l.message_seconds}s · {l.listened_fully ? "fully" : "not fully"}</span>}
                  {l.replays > 0 && <span className="text-xs">· replays {l.replays}</span>}
                </div>
                <ol className="mt-2 grid gap-1 text-xs">
                  {l.steps.map((st, i) => <li key={i} className="flex gap-2"><span className="tabular-nums text-muted-foreground">{fmtTime(st.at)}</span><span className="font-semibold">{st.event}</span><span className="text-muted-foreground">{st.note || st.key || ""}</span></li>)}
                </ol>
              </div>
            )}
          </div>
          {call?.alert && <div className={card} data-testid="sim-alert-view"><p className="mb-2 text-xs font-bold uppercase text-muted-foreground">Family alert · WhatsApp preview, not sent</p><p className="text-sm">To: {call.alert.to_name} {call.alert.to_masked}</p><p className="wa-bubble font-te mt-2 p-3 text-[13px]" data-testid="sim-alert-text">{call.alert.text}</p></div>}
          {flags && <div className={card}><p className="mb-3 font-bold">Flags for this patient</p><FlagList flags={flags} testid="sim-flags" /></div>}
        </div>
      </div>
    </div>
  );
}
